import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { TagMark } from '@/brand/TagMark';

/**
 * HeroAppPreview — scaled phone showing onboarding intro slide #1 of the actual app.
 *
 * Replaces the prior HeroBrandGrid (which the user correctly called out as a
 * useless logo dump). The user's screenshot of the app onboarding was the brief:
 * show the real product anatomy on the marketing hero so a Macedonian visitor
 * sees ДА/НЕ + EUR pricing + Cyrillic team names at-a-glance, not 62¢ rows or a
 * brand-system pinboard.
 *
 * Faithful port of `screen-onboarding.jsx` IntroVisual `visual1` + the surrounding
 * intro1 slide (eyebrow / headline / body / dots / CTA) at iPhone scale, wrapped
 * in a minimal phone shell (rounded bezel + dynamic island, no status-bar chrome).
 */
export default function HeroAppPreview(): JSX.Element {
  const { t } = useTranslation();

  return (
    <div
      className="mx-auto"
      style={{ width: 320 }}
      role="img"
      aria-label={t('hero.appPreview.alt')}
    >
      <div
        className="relative overflow-hidden"
        style={{
          width: 320,
          height: 660,
          borderRadius: 44,
          background: 'var(--color-ink)',
          boxShadow:
            '0 30px 60px rgba(0,0,0,0.25), 0 0 0 1px rgba(11,13,16,0.5), 0 0 0 7px rgba(5,6,8,1)',
        }}
      >
        {/* dynamic island */}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 9,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 92,
            height: 26,
            borderRadius: 16,
            background: 'rgba(0,0,0,1)',
            zIndex: 5,
          }}
        />

        <div className="absolute inset-0 flex flex-col pt-[44px]">
          {/* Header: PRDKT logo only — skip removed, primary CTA scrolls to #markets */}
          <div className="flex items-center px-4 pt-2">
            <TagMark size={16} dark ariaHidden />
          </div>

          {/* Stacked rotated market cards */}
          <div className="relative mx-auto mt-3" style={{ width: 280, height: 198 }}>
            {/* Top card — paper-2 surface, rotated left */}
            <div
              style={{
                position: 'absolute',
                top: 8,
                left: 0,
                right: 28,
                background: 'var(--color-paper-2)',
                borderRadius: 18,
                padding: '14px 16px',
                transform: 'rotate(-3deg)',
                boxShadow: '0 8px 22px rgba(0,0,0,0.30)',
              }}
            >
              <div
                className="font-mono text-[9px] tracking-[0.16em] uppercase"
                style={{ color: 'var(--color-dim)' }}
              >
                Премиер Лига
              </div>
              <div
                className="mt-1 font-sans text-[15px] leading-tight font-medium"
                style={{ color: 'var(--color-ink)', letterSpacing: '-0.3px' }}
              >
                Арсенал v Челси
              </div>
              <div className="mt-2.5 flex gap-1.5">
                <div
                  className="rounded-pill flex-1 text-center font-mono text-[11px] font-semibold"
                  style={{
                    padding: '7px 8px',
                    background: 'rgba(24,162,106,0.12)',
                    color: 'var(--color-green)',
                  }}
                >
                  ДА · €0.62
                </div>
                <div
                  className="rounded-pill flex-1 text-center font-mono text-[11px] font-semibold"
                  style={{
                    padding: '7px 8px',
                    background: 'rgba(216,58,58,0.10)',
                    color: 'var(--color-red)',
                  }}
                >
                  НЕ · €0.38
                </div>
              </div>
            </div>

            {/* Bottom card — paper surface, rotated right, offset down/right */}
            <div
              style={{
                position: 'absolute',
                top: 90,
                left: 28,
                right: 0,
                background: 'var(--color-paper)',
                borderRadius: 18,
                padding: '14px 16px',
                transform: 'rotate(2.5deg)',
                boxShadow: '0 8px 22px rgba(0,0,0,0.30)',
              }}
            >
              <div
                className="font-mono text-[9px] tracking-[0.16em] uppercase"
                style={{ color: 'var(--color-dim)' }}
              >
                Ла Лига
              </div>
              <div
                className="mt-1 font-sans text-[15px] leading-tight font-medium"
                style={{ color: 'var(--color-ink)', letterSpacing: '-0.3px' }}
              >
                Реал v Барса
              </div>
              <div className="mt-2.5 flex gap-1.5">
                <div
                  className="rounded-pill flex-1 text-center font-mono text-[11px] font-semibold"
                  style={{
                    padding: '7px 8px',
                    background: 'rgba(24,162,106,0.14)',
                    color: 'var(--color-green)',
                  }}
                >
                  ДА · €0.58
                </div>
                <div
                  className="rounded-pill flex-1 text-center font-mono text-[11px] font-semibold"
                  style={{
                    padding: '7px 8px',
                    background: 'rgba(216,58,58,0.12)',
                    color: 'var(--color-red)',
                  }}
                >
                  НЕ · €0.42
                </div>
              </div>
            </div>
          </div>

          {/* Copy block: eyebrow + headline + body */}
          <div className="px-5 pt-2">
            <div
              className="text-orange font-mono text-[10px] font-medium tracking-[0.18em] uppercase"
              style={{ marginBottom: 10 }}
            >
              {t('hero.appPreview.eyebrow')}
            </div>
            <h2
              className="text-paper m-0 font-sans text-[26px] leading-[1.0] font-semibold"
              style={{ letterSpacing: '-0.8px' }}
            >
              {t('hero.appPreview.title')}
              <br />
              <span style={{ color: 'rgba(244,241,234,0.55)' }}>
                {t('hero.appPreview.titleAccent')}
              </span>
            </h2>
            <p
              className="text-paper/70 mt-3 text-[12px] leading-[1.5]"
              style={{ letterSpacing: '0.1px' }}
            >
              {t('hero.appPreview.body')}
            </p>
          </div>

          {/* Dots + CTA */}
          <div className="mt-auto px-5 pb-7">
            <div className="mb-4 flex justify-center gap-1.5" aria-hidden="true">
              <span
                className="rounded-pill bg-paper"
                style={{ width: 18, height: 5, transition: 'all 0.25s' }}
              />
              <span
                className="rounded-pill"
                style={{ width: 5, height: 5, background: 'rgba(244,241,234,0.25)' }}
              />
              <span
                className="rounded-pill"
                style={{ width: 5, height: 5, background: 'rgba(244,241,234,0.25)' }}
              />
            </div>
            <a
              href="#markets"
              className="bg-paper text-ink rounded-pill flex h-11 w-full items-center justify-center font-sans text-[14px] font-semibold transition-opacity hover:opacity-90"
              style={{ letterSpacing: '0.16px' }}
            >
              {t('hero.appPreview.cta')}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
